/**
 * Accepting a teacher invitation (S45).
 *
 * `inviteTeacher` emails a link to `/teacher/accept-invite?token=...`. Until 2026-09-07
 * the SPA registered no such route, so every invitation landed on NotFound — and nothing
 * read the `teacherInvitations` collection either. Since the admin UI can only demote a
 * teacher and has no promote control, there was no path to a teacher account at all.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { logger } from '../../utils/logger';
import { LoadingSpinner } from '../Loading/LoadingSpinner';
import { invalidateRoleCache } from './RoleGuard';

type Phase = 'checking' | 'needs-signin' | 'accepting' | 'accepted' | 'failed';

export const AcceptTeacherInvite: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();

  const token = params.get('token') ?? '';
  const [phase, setPhase] = useState<Phase>('checking');
  const [message, setMessage] = useState('');

  const accept = useCallback(async () => {
    setPhase('accepting');
    try {
      const call = httpsCallable<
        { token: string },
        { success: boolean; role: string; alreadyAccepted?: boolean }
      >(functions.value, 'acceptTeacherInvitation');

      await call({ token });

      // The role arrives as a custom claim, so the ID token has to be refreshed
      // before any RoleGuard will see it.
      await currentUser?.getIdToken(true);

      // S66: RoleGuard also memoises the resolved role for five minutes, and its
      // `invalidateRoleCache` export had no callers anywhere. Without this a teacher who
      // had opened any guarded route before accepting — which the "Sign in" bounce makes
      // likely — was told "Access Denied" on the dashboard they had just been invited to,
      // for up to five minutes, with nothing to do about it.
      invalidateRoleCache(currentUser?.uid);

      setPhase('accepted');
    } catch (error) {
      // Deliberately surfaces the server's message rather than a generic one: the
      // server distinguishes "wrong email address" from "not a valid link", and the
      // person needs to know which.
      logger.error('Teacher invitation acceptance failed:', error);
      setMessage(
        error instanceof Error && error.message
          ? error.message.replace(/^.*?:\s*/, '')
          : 'That invitation could not be accepted.'
      );
      setPhase('failed');
    }
  }, [token, currentUser]);

  useEffect(() => {
    if (authLoading) return;
    if (!token) {
      setMessage('This link is missing its invitation token.');
      setPhase('failed');
      return;
    }
    if (!currentUser) {
      setPhase('needs-signin');
      return;
    }
    void accept();
  }, [authLoading, token, currentUser, accept]);

  if (authLoading || phase === 'checking' || phase === 'accepting') {
    return <LoadingSpinner />;
  }

  return (
    <div
      style={{ maxWidth: 560, margin: '4rem auto', padding: '0 1.5rem' }}
      role="status"
      aria-live="polite"
    >
      {phase === 'needs-signin' && (
        <>
          <h1>Accept your teacher invitation</h1>
          <p>
            Please sign in with the email address the invitation was sent to, then open
            this link again.
          </p>
          <button
            type="button"
            onClick={() =>
              navigate(`/login?next=${encodeURIComponent(`/teacher/accept-invite?token=${token}`)}`)
            }
          >
            Sign in
          </button>
        </>
      )}

      {phase === 'accepted' && (
        <>
          <h1>You&apos;re set up as a teacher</h1>
          <p>Your account now has teacher access to this school.</p>
          <button type="button" onClick={() => navigate('/teacher')}>
            Go to my dashboard
          </button>
        </>
      )}

      {phase === 'failed' && (
        <>
          <h1>That invitation didn&apos;t work</h1>
          {/* An honest failure, not a silent redirect to the dashboard. */}
          <p role="alert">{message}</p>
          <button type="button" onClick={() => navigate('/')}>
            Back to home
          </button>
        </>
      )}
    </div>
  );
};
